import React from 'react';
import api from '../api/apiClient';
import { isLookupActive } from './handleHighlight';
import { getImageUrl } from './mediaUrl';
import { getHomeLanguageCode } from './localeUtils';

export const getCaptionSourceLanguage = containerElement =>
    containerElement
        ?.closest('[data-source-language]')
        ?.getAttribute('data-source-language') || null;

// Utility function to extract full sentence context from the container element
const extractSentenceContext = (containerElement, selectedText) => {
    if (!containerElement) return null;

    try {
        // Get the parent container that holds all the spans
        const messageContainer =
            containerElement.closest('[data-message-content]') ||
            containerElement.parentNode;
        if (!messageContainer) return null;

        // Get all spans in the message
        const spans = Array.from(messageContainer.querySelectorAll('span'));
        if (!spans.length) return null;

        // Get the full text content
        const fullText = spans.map(span => span.textContent.trim()).join(' ');

        // Split into sentences using common sentence endings, but preserve the punctuation
        const sentences = fullText
            .split(/([.!?]+)/)
            .reduce((acc, part, index, array) => {
                if (index % 2 === 0) {
                    // This is sentence text
                    if (part.trim()) {
                        const punctuation = array[index + 1] || '';
                        acc.push((part.trim() + punctuation).trim());
                    }
                }
                return acc;
            }, []);

        // Find the sentence that contains the selected text (try with and without punctuation)
        const selectedTextLower = selectedText.toLowerCase().trim();
        const selectedSentence = sentences.find(sentence => {
            const sentenceLower = sentence.toLowerCase();
            return (
                sentenceLower.includes(selectedTextLower) ||
                sentenceLower.includes(selectedTextLower.replace(/[.!?]+$/, ''))
            );
        });

        return selectedSentence || null;
    } catch (error) {
        console.error('Error extracting sentence context:', error);
        return null;
    }
};

const wordLookUp = async ({
    clickedWord,
    topPos,
    midXPos,
    containerElement = null,
    tooltip,
    setShowWordLookupLoader = null,
    lookupId = null,
    onOpenGlossary = null,
    onLimitExceeded = null,
    onAddWord = null, // Callback for adding word to deck (only shown if user has bjorn account)
    showAddButton = false, // Whether to show the add button
    onTranslationSuccess = null,
}) => {
    // Show loader immediately and set tooltip position
    if (setShowWordLookupLoader) {
        setShowWordLookupLoader(true);
        tooltip.setTooltipPosition({
            top: topPos - 45,
            left: midXPos,
        });
    }

    try {
        // Extract sentence context for better translations
        const fullSentence = extractSentenceContext(containerElement, clickedWord);
        const captionSourceLanguage = getCaptionSourceLanguage(containerElement);

        // Call the captions-specific contextual word lookup API
        // For captions, we translate to English (homeLanguage)
        // Source language is auto-detected
        const homeLanguage = getHomeLanguageCode(); // 'US' or 'EN' based on locale
        const response = await api.post('/api/captions/contextualWordLookup', {
            selectedText: clickedWord,
            fullSentence,
            targetLanguage: captionSourceLanguage || 'auto',
            homeLanguage, // 'US' or 'EN'
        });

        const aiTranslation = response.data.contextualDefinition.replace(
            /[.,!;:]+$/,
            ''
        );
        const sourceLanguage =
            captionSourceLanguage || response.data.sourceLanguage || null;
        onTranslationSuccess?.(
            containerElement
                ?.closest('[data-analytics-session]')
                ?.getAttribute('data-analytics-session') || null
        );

        // Hide loader immediately when result comes in
        if (setShowWordLookupLoader) {
            setShowWordLookupLoader(false);
        }

        // Check if this lookup is still active - if not, don't show the tooltip
        if (lookupId && !isLookupActive(lookupId)) {
            return;
        }

        // Create tooltip content with the translation and search button
        const normalizedWord = clickedWord
            .replace(/\s*\([^)]*\)\s*/g, '')
            .replace(/[.,!;:]+$/, '')
            .trim()
            .toLowerCase();
        const normalizedDefinition = aiTranslation
            .replace(/[.,!;:]+$/, '')
            .toLowerCase();

        const tooltipContentWithIcon = (
            <>
                <button
                    className="tooltip-btn tooltip-btn-glossary tooltip-search"
                    onClick={e => {
                        e.stopPropagation();
                        onOpenGlossary?.({
                            word: normalizedWord,
                            definition: normalizedDefinition,
                            sourceLanguage,
                        });
                    }}
                >
                    <img src={getImageUrl("icon-search.svg")} alt="Search" />
                </button>
                <div className="tooltip-text">{aiTranslation}</div>
                {showAddButton && onAddWord && (
                    <button
                        className="tooltip-btn tooltip-btn-add"
                        onClick={e => {
                            e.stopPropagation();
                            onAddWord?.({
                                word: normalizedWord,
                                definition: normalizedDefinition,
                                sourceLanguage,
                            });
                        }}
                    >
                        <img src={getImageUrl("icon-plus.svg")} alt="Add" style={{ pointerEvents: 'none' }} />
                    </button>
                )}
            </>
        );
        tooltip.setTooltipContent(tooltipContentWithIcon);
        tooltip.setShowTooltip(true);
        tooltip.setTooltipPosition({
            top: topPos - 45,
            left: midXPos,
        });

        // Store tooltip data
        if (!lookupId || isLookupActive(lookupId)) {
            tooltip.setTooltipData({
                word: normalizedWord,
                definition: normalizedDefinition,
                sourceLanguage,
            });
        }
    } catch (error) {
        console.error('Word lookup error:', error);

        // Check for usage limit exceeded (429)
        if (error.response?.status === 429 && error.response?.data?.limitExceeded) {
            if (setShowWordLookupLoader) {
                setShowWordLookupLoader(false);
            }
            tooltip.setShowTooltip(false);
            if (onLimitExceeded) {
                onLimitExceeded(error.response.data);
            }
            return;
        }

        // Hide loader on error
        if (setShowWordLookupLoader) {
            setShowWordLookupLoader(false);
        }

        // Create error tooltip content
        const errorTooltipContent = (
            <>
                <button
                    className="tooltip-btn tooltip-btn-glossary tooltip-search"
                    onClick={e => {
                        e.stopPropagation();
                        onOpenGlossary?.({
                            word: clickedWord,
                            definition: '',
                        });
                    }}
                >
                    <img src={getImageUrl("icon-search.svg")} alt="Search" />
                </button>
                <div className="tooltip-text">Translation unavailable</div>
                {showAddButton && onAddWord && (
                    <button
                        className="tooltip-btn tooltip-btn-add"
                        onClick={e => {
                            e.stopPropagation();
                            onAddWord?.({
                                word: clickedWord,
                                definition: '',
                            });
                        }}
                    >
                        <img src={getImageUrl("icon-plus.svg")} alt="Add" style={{ pointerEvents: 'none' }} />
                    </button>
                )}
            </>
        );
        tooltip.setTooltipContent(errorTooltipContent);
        tooltip.setShowTooltip(true);
        tooltip.setTooltipPosition({
            top: topPos - 45,
            left: midXPos,
        });

        tooltip.setTooltipData({
            word: clickedWord,
            definition: 'Error',
        });
    }
};

export default wordLookUp;
